import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { lcCoachingApi } from '@/api/lcCoaching'
import { getApiErrorMessage } from '@/utils/apiError'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import type {
  BuildupStepGradeBatchRequest,
  CoachingLessonDetailResponse,
  ComponentProgressDetailView,
  LearnerTodayResponse,
} from '@/types/coaching'

/**
 * 빌드업 STEP 항목 OXΔ 일괄 채점 mutation.
 * - onSuccess: 서버가 돌려준 component detail 로 해당 컴포넌트 전체를 교체(권위 재조정 — 부착된 grade 포함).
 * - onError: 에러 토스트만 표시. UI draft 는 유지되어 그대로 재시도할 수 있다.
 */
export function useGradeBuildupStep(learnerId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation('coaching')
  const key = coachingKeys.learnerToday(learnerId)

  return useMutation({
    mutationFn: (body: BuildupStepGradeBatchRequest) =>
      lcCoachingApi.gradeBuildupSteps(learnerId, body),
    onError: (err) => {
      toast.error(getApiErrorMessage(err, t))
    },
    onSuccess: (updated: ComponentProgressDetailView, body) => {
      qc.setQueryData<LearnerTodayResponse>(key, (cur) =>
        cur ? spliceComponent(cur, updated) : cur,
      )
      qc.setQueriesData<CoachingLessonDetailResponse>(
        { queryKey: coachingKeys.lessonDetailPath(learnerId, body.lessonId) },
        (cur) => cur ? spliceDetailComponent(cur, updated) : cur,
      )
      void qc.invalidateQueries({ queryKey: coachingKeys.library(learnerId, 'all') })
      toast.success(t('buildup.grade.saved'))
    },
  })
}

// ── 순수 immutable 헬퍼 ──────────────────────────────────────────

/**
 * 권위 있는 재조정: configComponentId 가 일치하는 컴포넌트를 서버 응답 detail 로 통째 교체.
 */
function spliceComponent(
  data: LearnerTodayResponse,
  comp: ComponentProgressDetailView,
): LearnerTodayResponse {
  const lessons = data.lessons
  if (!lessons) return data
  return {
    ...data,
    lessons: lessons.map((lesson) => {
      const components = lesson.components
      if (!components) return lesson
      let changed = false
      const next = components.map((component) => {
        if (component.configComponentId !== comp.configComponentId) return component
        changed = true
        return comp
      })
      if (!changed) return lesson
      return { ...lesson, components: next }
    }),
  }
}

function spliceDetailComponent(
  data: CoachingLessonDetailResponse,
  comp: ComponentProgressDetailView,
): CoachingLessonDetailResponse {
  const lesson = data.initialAttempt
  const components = lesson.components
  if (!components) return data
  let changed = false
  const next = components.map((component) => {
    if (component.configComponentId !== comp.configComponentId) return component
    changed = true
    return comp
  })
  if (!changed) return data
  return { ...data, initialAttempt: { ...lesson, components: next } }
}
